import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Alert,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Save as SaveIcon,
  Warning as WarningIcon,
  Comment as CommentIcon,
} from '@mui/icons-material';
import PageWithHeader from '../common/PageWithHeader';
import LoadingCover from '../common/LoadingCover';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  useGetEmployeeReviewByIdQuery,
  usePatchSpearReviewByIdManagerEditMutation,
  usePatchSpearReviewByIdGoalStatusMutation,
  EmployeeReviewWithRelations,
  PerformanceItem,
  MonthlyIncentiveGoal,
} from '../../redux/vmsApi';
import { useGetUserByIdQuery } from '../../redux/vmsApi';
import { getGoalAchievementStatus, getGoalStatusColor } from '../../utils/goalUtils';

// Rating scale for performance items
const RATING_OPTIONS = ['A+', 'A', 'A-', 'B+', 'B', 'C+', 'C', 'C-', 'D', 'E', 'F'];

// Goal manager status options
const GOAL_STATUS_OPTIONS = [
  { value: 'pending', label: 'Pending Review', color: '#ff9800' },
  { value: 'approved', label: 'Approved', color: '#4caf50' },
  { value: 'disputed', label: 'Disputed', color: '#f44336' },
];

// Status colors
const getStatusColor = (status?: string): 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' => {
  switch (status) {
    case 'approved': return 'success';
    case 'submitted': return 'info';
    case 'under_review': return 'warning';
    case 'rejected': return 'error';
    default: return 'default';
  }
};

// Collapsible Section Component
interface CollapsibleSectionProps {
  title: string;
  children: React.ReactNode;
  defaultExpanded?: boolean;
}

const CollapsibleSection: React.FC<CollapsibleSectionProps> = ({ title, children, defaultExpanded = false }) => {
  const [expanded, setExpanded] = useState(defaultExpanded);

  return (
    <Paper sx={{ mb: 2 }}>
      <Box
        sx={{
          p: 2,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          cursor: 'pointer',
          backgroundColor: '#f5f5f5',
        }}
        onClick={() => setExpanded(!expanded)}
      >
        <Typography variant="h6" fontWeight="bold">{title}</Typography>
        <IconButton size="small">
          {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </IconButton>
      </Box>
      <Collapse in={expanded}>
        <Box sx={{ p: 2 }}>
          {children}
        </Box>
      </Collapse>
    </Paper>
  );
};

// Performance Item with Manager Rating
interface PerformanceItemRowProps {
  item: PerformanceItem;
  index: number;
  onManagerRatingChange: (index: number, rating: string) => void;
  isEditing?: boolean;
}

const PerformanceItemRow: React.FC<PerformanceItemRowProps> = ({ item, index, onManagerRatingChange, isEditing = true }) => (
  <TableRow>
    <TableCell sx={{ width: 50 }}>{item.ranking || index + 1}</TableCell>
    <TableCell>{item.title}</TableCell>
    <TableCell align="center" sx={{ width: 100 }}>
      <Chip label={item.rating || 'N/A'} size="small" variant="outlined" />
    </TableCell>
    <TableCell align="center" sx={{ width: 120 }}>
      {isEditing ? (
        <FormControl size="small" fullWidth>
          <Select
            value={item.managerRating || ''}
            onChange={(e) => onManagerRatingChange(index, e.target.value)}
            displayEmpty
          >
            <MenuItem value="">—</MenuItem>
            {RATING_OPTIONS.map((rating) => (
              <MenuItem key={rating} value={rating}>{rating}</MenuItem>
            ))}
          </Select>
        </FormControl>
      ) : (
        <Chip 
          label={item.managerRating || '—'} 
          size="small" 
          color={item.managerRating ? 'primary' : 'default'}
          variant="outlined" 
        />
      )}
    </TableCell>
  </TableRow>
);

// Main Component
const SPEARManagerReview: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const periodFromUrl = searchParams.get('period');

  // API Hooks
  const { data: review, isLoading, refetch } = useGetEmployeeReviewByIdQuery(
    { id: id || '' },
    { skip: !id }
  );
  const { data: employee } = useGetUserByIdQuery(
    { id: review?.userId || '' },
    { skip: !review?.userId }
  );
  const [managerEdit, { isLoading: isSaving }] = usePatchSpearReviewByIdManagerEditMutation();
  const [updateGoalStatus, { isLoading: isUpdatingGoal }] = usePatchSpearReviewByIdGoalStatusMutation();

  // Local state for edits
  const [accomplishments, setAccomplishments] = useState<PerformanceItem[]>([]);
  const [quarterlyGoals, setQuarterlyGoals] = useState<PerformanceItem[]>([]);
  const [monthlyGoals, setMonthlyGoals] = useState<PerformanceItem[]>([]);
  const [managerOverallComments, setManagerOverallComments] = useState('');
  const [managerAccomplishmentsComments, setManagerAccomplishmentsComments] = useState('');
  const [managerGoalsComments, setManagerGoalsComments] = useState('');
  const [goalManagerStatus, setGoalManagerStatus] = useState<'pending' | 'approved' | 'disputed'>('pending');
  const [goalManagerComment, setGoalManagerComment] = useState('');
  
  // Edit mode state - starts based on whether review has been saved by manager
  const [isEditing, setIsEditing] = useState(false);
  
  // Check if manager has already reviewed (has manager data)
  const hasBeenReviewed = Boolean(
    review?.managerEditedAt ||
    review?.managerOverallComments ||
    review?.accomplishments?.some(a => a.managerRating) ||
    review?.quarterlyGoals?.some(g => g.managerRating) ||
    review?.monthlyGoals?.some(g => g.managerRating)
  );
  
  // Dialog states
  const [goalDialogOpen, setGoalDialogOpen] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  // Initialize state from review data
  useEffect(() => {
    if (review) {
      setAccomplishments(review.accomplishments || []);
      setQuarterlyGoals(review.quarterlyGoals || []);
      setMonthlyGoals(review.monthlyGoals || []);
      setManagerOverallComments(review.managerOverallComments || '');
      setManagerAccomplishmentsComments(review.managerAccomplishmentsComments || '');
      setManagerGoalsComments(review.managerGoalsComments || '');
      setGoalManagerStatus(review.monthlyIncentiveGoal?.managerStatus || 'pending');
      setGoalManagerComment(review.monthlyIncentiveGoal?.managerComment || '');
      
      // Start in edit mode only if not yet reviewed by manager
      const reviewed = Boolean(
        review.managerEditedAt ||
        review.managerOverallComments ||
        review.accomplishments?.some(a => a.managerRating) ||
        review.quarterlyGoals?.some(g => g.managerRating) ||
        review.monthlyGoals?.some(g => g.managerRating)
      );
      setIsEditing(!reviewed);
    }
  }, [review]);

  // Handlers for performance item rating changes
  const handleAccomplishmentRatingChange = (index: number, rating: string) => {
    const updated = [...accomplishments];
    updated[index] = { ...updated[index], managerRating: rating };
    setAccomplishments(updated);
  };

  const handleQuarterlyGoalRatingChange = (index: number, rating: string) => {
    const updated = [...quarterlyGoals];
    updated[index] = { ...updated[index], managerRating: rating };
    setQuarterlyGoals(updated);
  };

  const handleMonthlyGoalRatingChange = (index: number, rating: string) => {
    const updated = [...monthlyGoals];
    updated[index] = { ...updated[index], managerRating: rating };
    setMonthlyGoals(updated);
  };

  // Save changes
  const handleSave = async () => {
    if (!id) return;
    
    try {
      await managerEdit({
        id,
        employeeReviewPartialExcludingIdUserIdReviewPeriodCreatedAt: {
          accomplishments,
          quarterlyGoals,
          monthlyGoals,
          managerOverallComments,
          managerAccomplishmentsComments,
          managerGoalsComments,
        },
      }).unwrap();
      
      setSuccessMessage('SPEAR Review saved and approved successfully');
      setIsEditing(false); // Switch to read-only mode after save
      setTimeout(() => setSuccessMessage(''), 3000);
      refetch();
    } catch (error: any) {
      setErrorMessage(error?.data?.error?.message || 'Failed to save changes');
      setTimeout(() => setErrorMessage(''), 5000);
    }
  };

  // Update goal status
  const handleGoalStatusUpdate = async () => {
    if (!id) return;
    
    // Require comment if disputing
    if (goalManagerStatus === 'disputed' && !goalManagerComment.trim()) {
      setErrorMessage('Comment is required when disputing a goal');
      setTimeout(() => setErrorMessage(''), 3000);
      return;
    }
    
    try {
      await updateGoalStatus({
        id,
        body: {
          managerStatus: goalManagerStatus,
          managerComment: goalManagerComment,
        },
      }).unwrap();
      
      setGoalDialogOpen(false);
      setSuccessMessage('Goal status updated successfully');
      setTimeout(() => setSuccessMessage(''), 3000);
      refetch();
    } catch (error: any) {
      setErrorMessage(error?.data?.error?.message || 'Failed to update goal status');
      setTimeout(() => setErrorMessage(''), 5000);
    }
  };

  if (isLoading) {
    return (
      <PageWithHeader>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
          <LoadingCover />
        </Box>
      </PageWithHeader>
    );
  }

  if (!review) {
    return (
      <PageWithHeader>
        <Box sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="error">SPEAR Review not found</Typography>
          <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(-1)} sx={{ mt: 2 }}>
            Go Back
          </Button>
        </Box>
      </PageWithHeader>
    );
  }

  const goal = review.monthlyIncentiveGoal;
  const goalStatus = getGoalAchievementStatus(goal);
  const goalColor = getGoalStatusColor(goalStatus);
  // Allow editing if not already approved/rejected (includes legacy entries without status)
  const canEdit = !review.status || review.status === 'draft' || review.status === 'submitted' || review.status === 'under_review';

  return (
    <PageWithHeader>
      <Box sx={{ maxWidth: 1200, mx: 'auto', p: 3 }}>
        {/* Header */}
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
          <Box>
            <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(-1)} sx={{ mb: 1 }}>
              Back
            </Button>
            <Typography variant="h4" fontWeight="bold" color="primary">
              SPEAR Manager Review
            </Typography>
            <Typography variant="body1" color="text.secondary">
              {review.reviewPeriod}
            </Typography>
          </Box>
          <Stack direction="row" spacing={2} alignItems="center">
            <Chip
              label={review.status || 'draft'}
              color={getStatusColor(review.status)}
              variant="outlined"
            />
            {canEdit && !isEditing && (
              <Button
                variant="outlined"
                color="primary"
                onClick={() => setIsEditing(true)}
              >
                Edit
              </Button>
            )}
          </Stack>
        </Stack>

        {/* Alerts */}
        {successMessage && <Alert severity="success" sx={{ mb: 2 }}>{successMessage}</Alert>}
        {errorMessage && <Alert severity="error" sx={{ mb: 2 }}>{errorMessage}</Alert>}

        {/* Employee Info Card */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" color="text.secondary">Employee</Typography>
              <Typography variant="h6" fontWeight="bold">{employee?.name || 'Unknown'}</Typography>
              <Typography variant="body2" color="text.secondary">{employee?.email}</Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" color="text.secondary">Department</Typography>
              <Typography variant="body1">{employee?.department || 'N/A'}</Typography>
              <Typography variant="body2" color="text.secondary">
                Submitted: {review.submittedAt ? new Date(review.submittedAt).toLocaleDateString() : 'N/A'}
              </Typography>
            </Grid>
          </Grid>
        </Paper>

        {/* Monthly Incentive Goal Section */}
        {goal?.description && (
          <Paper sx={{ p: 3, mb: 3, bgcolor: `${goalColor}15`, borderLeft: `4px solid ${goalColor}` }}>
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
              <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle2" fontWeight="bold" color="text.secondary">
                  🎯 Monthly Incentive Goal (50% Bonus)
                </Typography>
                <Typography variant="h6" fontWeight="bold" sx={{ mt: 0.5 }}>
                  {goal.description}
                </Typography>
                {goal.measure && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    Measure: {goal.measure}
                  </Typography>
                )}
                <Stack direction="row" spacing={3} sx={{ mt: 1 }}>
                  <Typography variant="body2">
                    Target: <strong>{goal.targetPercentage || 100}%</strong>
                  </Typography>
                  {goal.actualPercentage !== undefined && (
                    <Typography variant="body2">
                      Actual: <strong style={{ color: goalColor }}>{goal.actualPercentage}%</strong>
                    </Typography>
                  )}
                </Stack>
                
                {/* Manager Goal Status */}
                <Box sx={{ mt: 2, p: 2, bgcolor: 'white', borderRadius: 1 }}>
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Typography variant="subtitle2">Manager Review:</Typography>
                    <Chip
                      size="small"
                      label={GOAL_STATUS_OPTIONS.find(o => o.value === (goal.managerStatus || 'pending'))?.label || 'Pending'}
                      sx={{
                        bgcolor: GOAL_STATUS_OPTIONS.find(o => o.value === (goal.managerStatus || 'pending'))?.color,
                        color: 'white',
                      }}
                    />
                    {canEdit && (
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<CommentIcon />}
                        onClick={() => setGoalDialogOpen(true)}
                      >
                        Review Goal
                      </Button>
                    )}
                  </Stack>
                  {goal.managerComment && (
                    <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic' }}>
                      "{goal.managerComment}"
                    </Typography>
                  )}
                </Box>
              </Box>
              <Chip
                label={goalStatus === 'achieved' ? 'Achieved' : goalStatus === 'progress' ? 'Progress' : goalStatus === 'failed' ? 'Failed' : 'Pending'}
                size="medium"
                sx={{ bgcolor: goalColor, color: 'white', fontWeight: 'bold', ml: 2 }}
              />
            </Stack>
          </Paper>
        )}

        {/* Accomplishments Section */}
        <CollapsibleSection title="Accomplishments" defaultExpanded>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                  <TableCell sx={{ fontWeight: 'bold' }}>#</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Title</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold' }}>Self Rating</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold' }}>Manager Rating</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {accomplishments.map((item, index) => (
                  <PerformanceItemRow
                    key={index}
                    item={item}
                    index={index}
                    onManagerRatingChange={handleAccomplishmentRatingChange}
                    isEditing={isEditing && canEdit}
                  />
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          {isEditing && canEdit ? (
            <TextField
              fullWidth
              multiline
              rows={2}
              label="Manager Comments on Accomplishments"
              value={managerAccomplishmentsComments}
              onChange={(e) => setManagerAccomplishmentsComments(e.target.value)}
              sx={{ mt: 2 }}
            />
          ) : managerAccomplishmentsComments ? (
            <Box sx={{ mt: 2, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
              <Typography variant="subtitle2" color="text.secondary">Manager Comments</Typography>
              <Typography variant="body1">{managerAccomplishmentsComments}</Typography>
            </Box>
          ) : null}
        </CollapsibleSection>

        {/* Quarterly Goals Section */}
        <CollapsibleSection title="Quarterly Goals" defaultExpanded>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                  <TableCell sx={{ fontWeight: 'bold' }}>#</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Goal</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold' }}>Self Rating</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold' }}>Manager Rating</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {quarterlyGoals.map((item, index) => (
                  <PerformanceItemRow
                    key={index}
                    item={item}
                    index={index}
                    onManagerRatingChange={handleQuarterlyGoalRatingChange}
                    isEditing={isEditing && canEdit}
                  />
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CollapsibleSection>

        {/* Monthly Goals Section */}
        <CollapsibleSection title="Next Month's Goals">
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                  <TableCell sx={{ fontWeight: 'bold' }}>#</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Goal</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold' }}>Self Rating</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold' }}>Manager Rating</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {monthlyGoals.map((item, index) => (
                  <PerformanceItemRow
                    key={index}
                    item={item}
                    index={index}
                    onManagerRatingChange={handleMonthlyGoalRatingChange}
                    isEditing={isEditing && canEdit}
                  />
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          {isEditing && canEdit ? (
            <TextField
              fullWidth
              multiline
              rows={2}
              label="Manager Comments on Goals"
              value={managerGoalsComments}
              onChange={(e) => setManagerGoalsComments(e.target.value)}
              sx={{ mt: 2 }}
            />
          ) : managerGoalsComments ? (
            <Box sx={{ mt: 2, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
              <Typography variant="subtitle2" color="text.secondary">Manager Comments</Typography>
              <Typography variant="body1">{managerGoalsComments}</Typography>
            </Box>
          ) : null}
        </CollapsibleSection>

        {/* Other Details Section */}
        <CollapsibleSection title="Other Details">
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" color="text.secondary">Area to Improve</Typography>
              <Typography variant="body1">{review.areaToImprove}</Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" color="text.secondary">Quarterly Objective</Typography>
              <Typography variant="body1">{review.quarterlyObjective}</Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" color="text.secondary">Examples of Excellence</Typography>
              <Typography variant="body1">{review.examplesOfExcellence}</Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" color="text.secondary">Main Goal to Achieve</Typography>
              <Typography variant="body1">{review.mainGoalToAchieve}</Typography>
            </Grid>
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Stack direction="row" spacing={4}>
                <Chip
                  label={`Turn up on time: ${review.turnUpOnTime ? 'Yes' : 'No'}`}
                  color={review.turnUpOnTime ? 'success' : 'error'}
                  variant="outlined"
                />
                <Chip
                  label={`Daily check-in: ${review.dailyCheckIn ? 'Yes' : 'No'}`}
                  color={review.dailyCheckIn ? 'success' : 'error'}
                  variant="outlined"
                />
                <Chip
                  label={`Reports on time: ${review.reportsOnTime ? 'Yes' : 'No'}`}
                  color={review.reportsOnTime ? 'success' : 'error'}
                  variant="outlined"
                />
              </Stack>
            </Grid>
          </Grid>
        </CollapsibleSection>

        {/* Manager Overall Comments */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" fontWeight="bold" gutterBottom>
            Manager Overall Comments
          </Typography>
          {isEditing && canEdit ? (
            <TextField
              fullWidth
              multiline
              rows={4}
              value={managerOverallComments}
              onChange={(e) => setManagerOverallComments(e.target.value)}
              placeholder="Enter your overall assessment and feedback..."
            />
          ) : managerOverallComments ? (
            <Typography variant="body1">{managerOverallComments}</Typography>
          ) : (
            <Typography variant="body2" color="text.secondary" fontStyle="italic">No comments yet</Typography>
          )}
          {review.managerEditedAt && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              Last reviewed: {new Date(review.managerEditedAt).toLocaleString()}
            </Typography>
          )}
        </Paper>

        {/* Save Button */}
        {isEditing && canEdit && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
            <Button
              variant="outlined"
              onClick={() => {
                // Reset to saved values
                if (review) {
                  setAccomplishments(review.accomplishments || []);
                  setQuarterlyGoals(review.quarterlyGoals || []);
                  setMonthlyGoals(review.monthlyGoals || []);
                  setManagerOverallComments(review.managerOverallComments || '');
                  setManagerAccomplishmentsComments(review.managerAccomplishmentsComments || '');
                  setManagerGoalsComments(review.managerGoalsComments || '');
                }
                setIsEditing(false);
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="success"
              startIcon={<SaveIcon />}
              onClick={handleSave}
              disabled={isSaving}
            >
              {isSaving ? 'Saving...' : 'Save & Approve'}
            </Button>
          </Box>
        )}

        {/* Goal Status Dialog */}
        <Dialog open={goalDialogOpen} onClose={() => setGoalDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Review Monthly Incentive Goal</DialogTitle>
          <DialogContent>
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" color="text.secondary">Goal</Typography>
              <Typography variant="body1" fontWeight="bold">{goal?.description}</Typography>
            </Box>
            
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Status</InputLabel>
              <Select
                value={goalManagerStatus}
                label="Status"
                onChange={(e) => setGoalManagerStatus(e.target.value as 'pending' | 'approved' | 'disputed')}
              >
                {GOAL_STATUS_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Box
                        sx={{
                          width: 12,
                          height: 12,
                          borderRadius: '50%',
                          bgcolor: option.color,
                        }}
                      />
                      <span>{option.label}</span>
                    </Stack>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            
            <TextField
              fullWidth
              multiline
              rows={3}
              label={goalManagerStatus === 'disputed' ? 'Comment (Required)' : 'Comment (Optional)'}
              value={goalManagerComment}
              onChange={(e) => setGoalManagerComment(e.target.value)}
              required={goalManagerStatus === 'disputed'}
              helperText={goalManagerStatus === 'disputed' ? 'Please explain why you are disputing this goal' : ''}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setGoalDialogOpen(false)}>Cancel</Button>
            <Button
              variant="contained"
              onClick={handleGoalStatusUpdate}
              disabled={isUpdatingGoal || (goalManagerStatus === 'disputed' && !goalManagerComment.trim())}
            >
              {isUpdatingGoal ? 'Updating...' : 'Update Status'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </PageWithHeader>
  );
};

export default SPEARManagerReview;
